import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  createDriveFolder,
  deleteDriveFolder,
  listDriveCompanySummaries,
  listDriveFiles,
  listDriveFolders,
  listUnplacedFiles,
  moveDriveFiles,
  placeFileInCompany,
  renameDriveFolder,
  type ListDriveFilesParams,
} from '@/api/services/drive.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { documentQueryKeys } from './useDocuments'

/** Under the company key, so uploads and file actions refresh the Drive too. */
const driveQueryKeys = {
  folders: (companyId: string) =>
    [...documentQueryKeys.company(companyId), 'drive', 'folders'] as const,
  files: (companyId: string, params: ListDriveFilesParams) =>
    [...documentQueryKeys.company(companyId), 'drive', 'files', params] as const,
}

export function useDriveCompanySummaries() {
  return useQuery({
    queryKey: [...documentQueryKeys.all, 'drive', 'summary'] as const,
    queryFn: listDriveCompanySummaries,
    staleTime: 60 * 1000,
  })
}

export function useDriveFolders(companyId: string, enabled = true) {
  return useQuery({
    queryKey: driveQueryKeys.folders(companyId),
    queryFn: () => listDriveFolders(companyId),
    enabled,
    staleTime: 60 * 1000,
  })
}

export function useDriveFiles(companyId: string, params: ListDriveFilesParams, enabled = true) {
  return useQuery({
    queryKey: driveQueryKeys.files(companyId, params),
    queryFn: () => listDriveFiles(companyId, params),
    enabled,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  })
}

function useFolderMutation<Input>(
  companyId: string,
  mutationFn: (input: Input) => Promise<void>,
  successMessage: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentQueryKeys.company(companyId) })
      toast.success(successMessage)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useMoveDriveFiles(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ fileIds, folderId }: { fileIds: string[]; folderId: string }) =>
      moveDriveFiles(companyId, fileIds, folderId),
    onSuccess: (_result, { fileIds }) => {
      queryClient.invalidateQueries({ queryKey: documentQueryKeys.company(companyId) })
      toast.success(fileIds.length === 1 ? 'File moved' : `${fileIds.length} files moved`)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useUnplacedFiles(page: number, limit: number, enabled = true) {
  return useQuery({
    queryKey: [...documentQueryKeys.all, 'drive', 'unplaced', page, limit] as const,
    queryFn: () => listUnplacedFiles(page, limit),
    enabled,
    staleTime: 60 * 1000,
    retry: false,
    placeholderData: keepPreviousData,
  })
}

export function usePlaceFileInCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, companyId }: { id: string; companyId: string }) =>
      placeFileInCompany(id, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentQueryKeys.all })
      toast.success('File placed')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useCreateDriveFolder(companyId: string) {
  return useFolderMutation(
    companyId,
    (input: { name: string; parentId?: string }) => createDriveFolder(companyId, input),
    'Folder created',
  )
}

export function useRenameDriveFolder(companyId: string) {
  return useFolderMutation(
    companyId,
    ({ id, name }: { id: string; name: string }) => renameDriveFolder(companyId, id, name),
    'Folder renamed',
  )
}

export function useDeleteDriveFolder(companyId: string) {
  return useFolderMutation(
    companyId,
    (id: string) => deleteDriveFolder(companyId, id),
    'Folder deleted',
  )
}
